import "@tento-ui/components/round";

import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css } from "@tento-ui/component-styles";
import { DomUtils } from "@tento-ui/dom";
import { RadioButtonCell } from "@tento-ui/components/radio-button-cell";
import { Round } from "@tento-ui/components/round";
import {
  UserCalendarList,
  CalendarRowVisibilityIcon,
} from "@tento-lona/sheets-ui";
import {
  AuthScopedCalendarId,
  AuthScopedCalendarDisplay,
} from "@tento-lona";
import { CalendarsClient } from "@tento-lona";
import { Network } from "@tento-lona/network";
import { CalendarProviders } from "../settings/calendar-providers";

@component({
  name: "lona-calendar-visibility-selector",
})
export class CalendarVisibilitySelector extends LonaWebComponent {
  private visibilityMode: "default" | "custom" = "default";
  private customCalendars: AuthScopedCalendarId[] = [];
  private calendars: readonly Network.CalendarList[] = [];
  private _calendarClient: Option<CalendarsClient> = null;
  private onChange: Option<
    (
      mode: "default" | "custom",
      customCalendars: AuthScopedCalendarId[],
    ) => void
  >;

  private $defaultRadio: RadioButtonCell = this.hydrate("default-radio");
  private $customRadio: RadioButtonCell = this.hydrate("custom-radio");
  private $customSubtitle: HTMLElement = this.$("custom-subtitle");
  private $calendarListContainer: HTMLElement = this.$(
    "calendar-list-container",
  );
  private $round: Round = this.hydrate<Round>("calendar-list-bg")
    .bind({
      bg: "var(--bg-surface-light)",
      continuous: { smoothing: 0.6 },
      radius: 16,
      shadow: false,
    });

  private radioGroup = new RadioButtonCell.Group({
    onSelected: (selected) => {
      const isCustom = selected === this.$customRadio;
      this.$customSubtitle.style.display = isCustom ? "flex" : "none";

      const newMode = isCustom ? "custom" : "default";
      this.onChange?.(newMode, [...this.customCalendars]);
      return true;
    },
  });

  connectedCallback() {
    this.$defaultRadio.bind({ title: "Default Visibility" });
    this.$customRadio.bind({ title: "Custom Visibility" });
    this.radioGroup.bind([this.$defaultRadio, this.$customRadio]);
  }

  bind(
    visibilityMode: "default" | "custom",
    customCalendars: AuthScopedCalendarId[],
    onChange: (
      mode: "default" | "custom",
      customCalendars: AuthScopedCalendarId[],
    ) => void,
    calendarClient: CalendarsClient,
  ): this {
    this.visibilityMode = visibilityMode;
    this.customCalendars = customCalendars;
    this.onChange = onChange;
    this._calendarClient = calendarClient;
    // A selector can bind before the boot payload loaded the calendars.
    this.calendars = calendarClient.isLoaded() ? calendarClient.peekAll() : [];
    this.render();
    return this;
  }

  private hasCalendar(calendarId: AuthScopedCalendarId): boolean {
    return this.customCalendars.some(
      ([authId, id]) => authId === calendarId[0] && id === calendarId[1],
    );
  }

  private toggleCalendar(calendarId: AuthScopedCalendarId): void {
    const index = this.customCalendars.findIndex(
      ([authId, id]) => authId === calendarId[0] && id === calendarId[1],
    );
    if (index >= 0) {
      this.customCalendars.splice(index, 1);
    } else {
      this.customCalendars.push(calendarId);
    }
  }

  private render(): void {
    if (this.visibilityMode === "default") {
      this.radioGroup.select(this.$defaultRadio);
      this.$customSubtitle.style.display = "none";
    } else {
      this.radioGroup.select(this.$customRadio);
      this.$customSubtitle.style.display = "flex";
    }

    DomUtils.clearChildren(this.$calendarListContainer);
    const $calendarList = UserCalendarList.makeWith(this.calendars, {
      providerIcon: (source) => CalendarProviders.get(source)?.iconMono ?? null,
      accessoryFactory: (acal: AuthScopedCalendarDisplay) => {
        const calendarId = AuthScopedCalendarId.of(acal);

        const $icon = CalendarRowVisibilityIcon.makeWith(acal, this._calendarClient!, (_, __) => {
          this.toggleCalendar(calendarId);
          this.onChange?.("custom", [...this.customCalendars]);
          $icon.toggleAttribute("checked");
        });

        $icon.toggleAttribute("checked", this.hasCalendar(calendarId));

        return $icon;
      },
    });

    this.$calendarListContainer.appendChild($calendarList);
  }

  static $styles = [
    css`
      :host {
        padding: 12px 8px;
      }

      #custom-subtitle {
        margin-top: 8px;
        flex-direction: column;
        position: relative;
      }

      #calendar-list-bg {
        width: 100%;
      }

      #calendar-list-container {
        position: relative;
        z-index: 1;
        padding: 12px 16px;
      }
    `,
  ];

  static $html = html`
    <std-col id="root">
      <std-radio-button-cell id="default-radio"></std-radio-button-cell>
      <std-radio-button-cell id="custom-radio"></std-radio-button-cell>
      <std-col id="custom-subtitle">
        <std-round id="calendar-list-bg" floating></std-round>
        <std-col id="calendar-list-container"></std-col>
      </std-col>
    </std-col>
  `;
}
